/*
 * Product page helpers.
 *
 * 1. Tabs relocation (>=700px): the product tabs are authored as their own
 *    section after main-product; on tablet/desktop we move them into the
 *    info column's block list (under the variant picker, above the price +
 *    add-to-cart bar) and restore them below 700px. Ordering/styling lives
 *    in asheis.css (the matching @media (min-width: 700px) block).
 *
 * 2. Sticky bar fade (mobile, <700px): the floating price/add-to-cart bar
 *    stays fully visible through the product + its tabs AND the next section's
 *    padding, then fades out only once the "You May Also Like" (related
 *    products) content has scrolled up to the middle of the viewport — fully
 *    gone by the time it reaches the top.
 *
 * 3. Variant underline replay: keeps the option-underline wipe alive across
 *    Prestige's variant re-renders (colour switches replace the label nodes,
 *    which would otherwise cut the CSS transition short).
 */
(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function setupTabRelocation() {
    var tabs = document.querySelector('.shopify-section--product-tabs');
    var list = document.querySelector('.product-info__block-list');
    if (!tabs || !list) return;

    // Remember where the tabs section originally lived so we can put it
    // back when the viewport drops below the desktop breakpoint.
    var placeholder = document.createComment('product-tabs-original-position');
    tabs.parentNode.insertBefore(placeholder, tabs);

    var mq = window.matchMedia('(min-width: 700px)');

    function apply() {
      if (mq.matches) {
        if (tabs.parentElement !== list) list.appendChild(tabs);
      } else if (tabs.parentElement === list && placeholder.parentNode) {
        placeholder.parentNode.insertBefore(tabs, placeholder);
      }
    }

    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else if (mq.addListener) mq.addListener(apply);
  }

  function setupStickyBarFade() {
    var bar = document.querySelector('.product-sticky-bar');
    var rel = document.querySelector('.shopify-section--related-products');
    if (!bar || !rel) return;

    // Fade against the section's CONTENT box (the `.container` inside the
    // `.section-spacing` padding) rather than the section's outer edge, so the
    // bar stays fully visible while the next section's padding-top scrolls past
    // and only starts fading once the "You May Also Like" content itself
    // reaches the viewport bottom.
    var fadeTarget = rel.querySelector('.container') || rel;

    var mqMobile = window.matchMedia('(max-width: 699px)');

    function update() {
      // Only the mobile floating bar fades; on >=700px it's hidden via CSS.
      if (!mqMobile.matches) {
        bar.style.removeProperty('opacity');
        bar.style.removeProperty('pointer-events');
        return;
      }
      var vh = window.innerHeight;
      var top = fadeTarget.getBoundingClientRect().top; // related content top, viewport coords
      // Keep the bar fully visible all the way through the product, its (tall)
      // tab section, AND the next section's padding. Only start fading once the
      // "You May Also Like" content has scrolled up to the MIDDLE of the
      // viewport — i.e. it's the thing you're now actually looking at — and
      // finish fading as it reaches the top. Anchoring to the viewport middle
      // (rather than the content merely peeking in at the bottom edge, which
      // happens while the tab section is still centred) is what keeps the bar
      // up while you're still reading the tabs. Tuning knob: lower `fadeStart`
      // to fade later, raise it toward `vh` to fade earlier.
      var fadeStart = vh / 2;
      var opacity;
      if (top >= fadeStart) {
        opacity = 1;
      } else if (top <= 0) {
        opacity = 0;
      } else {
        opacity = top / fadeStart;
      }
      bar.style.opacity = opacity.toFixed(3);
      bar.style.pointerEvents = opacity < 0.05 ? 'none' : 'auto';
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; update(); });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* 3. Variant underline replay: selecting a value animates the underline via
     a CSS transition (same wipe as the product tabs — see asheis.css). Size
     switches morph the DOM in place, so the transition plays naturally. But a
     COLOUR switch re-renders the option values within ~60ms, replacing the
     label nodes and cutting the animation short — it read as an instant snap.
     Replay the identical wipe on the replacement nodes with the Web Animations
     API (pseudo-element animations), only for labels that are genuinely new,
     so surviving nodes never double-animate. */
  function setupVariantUnderlineReplay() {
    var root = document.querySelector('.shopify-section--main-product');
    if (!root || !Element.prototype.animate) return;
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var seen = new WeakSet();
    var prevChecked = []; // per option group: selected value text
    var pending = [];     // per option group: {from, to, at} of the last user switch

    function groups() {
      return Array.prototype.slice.call(root.querySelectorAll('.variant-picker__option-values'));
    }

    function checkedText(group) {
      var input = group.querySelector('input:checked');
      var label = input && input.nextElementSibling;
      return label ? label.textContent.trim() : null;
    }

    function wipe(label, from, to) {
      try {
        label.animate(
          [{ transform: 'scaleX(' + from + ')' }, { transform: 'scaleX(' + to + ')' }],
          { duration: 220, easing: 'ease', pseudoElement: '::after' }
        );
      } catch (e) { /* pseudoElement unsupported: the CSS resting state is still correct */ }
    }

    // Initial pass: record selections, mark nodes.
    groups().forEach(function (group, i) {
      prevChecked[i] = checkedText(group);
      Array.prototype.forEach.call(group.querySelectorAll('.block-swatch'), function (l) { seen.add(l); });
    });

    // A user switch flips the radio immediately — the CSS transition starts on
    // the current nodes. Record the from→to intent; if a re-render replaces
    // the nodes mid-transition (colour switches do, within ~60ms), the
    // observer below replays the same wipe on the replacements.
    root.addEventListener('change', function (e) {
      if (!e.target.closest || !e.target.closest('.variant-picker__option-values')) return;
      groups().forEach(function (group, i) {
        var text = checkedText(group);
        if (text !== prevChecked[i]) {
          pending[i] = { from: prevChecked[i], to: text, at: Date.now() };
          prevChecked[i] = text;
        }
      });
    });

    function onRerender() {
      groups().forEach(function (group, i) {
        var hasNew = false;
        Array.prototype.forEach.call(group.querySelectorAll('.block-swatch'), function (l) {
          if (!seen.has(l)) { seen.add(l); hasNew = true; }
        });

        var currentText = checkedText(group);
        var p = pending[i];
        prevChecked[i] = currentText;

        // The morph can land in several mutation batches; keep the pending
        // intent alive until the batch that actually replaces the labels
        // arrives (or it goes stale), otherwise an early no-op pass would
        // consume it and the replay would never fire.
        if (p && Date.now() - p.at > 300) { pending[i] = null; p = null; }

        // Replay only when the nodes were genuinely replaced while a fresh
        // user switch was animating; surviving nodes keep their CSS
        // transition and must not double-animate.
        if (!hasNew || reducedMotion || !p || p.to !== currentText) return;
        pending[i] = null;

        Array.prototype.forEach.call(group.querySelectorAll('.block-swatch'), function (label) {
          var t = label.textContent.trim();
          if (t === p.to) wipe(label, 0, 1);       // new selection slides in
          else if (t === p.from) wipe(label, 1, 0); // previous selection pushes out
        });
      });
    }

    var scheduled = false;
    var observer = new MutationObserver(function (mutations) {
      var touchesPicker = mutations.some(function (m) {
        var t = m.target;
        return t.nodeType === 1 && (t.closest('.variant-picker') || (t.querySelector && t.querySelector('.variant-picker')));
      });
      if (!touchesPicker || scheduled) return;
      scheduled = true;
      setTimeout(function () { scheduled = false; onRerender(); }, 0);
    });
    observer.observe(root, { childList: true, subtree: true });
  }

  ready(function () {
    setupTabRelocation();
    setupStickyBarFade();
    setupVariantUnderlineReplay();
  });
})();
